import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["page", "setPage", "isAnyFilterSelected"];
import I18n from 'I18n';
// @ts-ignore Could not find a declaration file for module
import { useSearchParam } from '../../hooks/searchParamHooks';
import { useUserPersonas } from '../persona/hooks';
import { FILTER_TYPES, FILTER_TYPES_OTHER } from '../../constants/TopFilterConstants';
import getLang from '../../utils/getLang';
import { CategoriesSubSections, HubspotToolsSubSections, SEARCH_FILTERS } from './searchConsts';
import { useLocation } from 'quartz-routing/react-router';
import { useLearningContentCardModels } from '../cards/hooks/useLearningContentCardModels';
import { usePagination } from '../pagination/paginationHooks';
import { PAGE_SIZE } from '../pagination/paginationConsts';
import { useSearch } from './hooks/useSearch';
export const useSearchPlaceholder = () => {
  const {
    mainPersona
  } = useUserPersonas();
  return mainPersona !== 'DEFAULT' ? I18n.text('academyComponents.getStarted.placeholderWithPersona', {
    persona: mainPersona.toLowerCase()
  }) : I18n.text('academyComponents.getStarted.placeholder');
};
export const useCatalogSearchParams = () => {
  const {
    search: searchParams
  } = useLocation();
  const {
    currentPage: page,
    setPage
  } = usePagination();
  const [languages] = useSearchParam(FILTER_TYPES.LANGUAGE);
  const [[search]] = useSearchParam(FILTER_TYPES_OTHER.SEARCH);
  const [categories] = useSearchParam(FILTER_TYPES_OTHER.CATEGORIES);
  const [durations] = useSearchParam(FILTER_TYPES.DURATION);
  const [hubSpotSoftware] = useSearchParam(FILTER_TYPES_OTHER.TOOLS);
  const [levels] = useSearchParam(FILTER_TYPES.LEVEL);
  const [contentTypes] = useSearchParam(FILTER_TYPES.CONTENT_TYPE);
  const [[contentSource]] = useSearchParam(FILTER_TYPES.CONTENT_SOURCE);
  const [[hasAwardsOnly]] = useSearchParam(FILTER_TYPES.AWARD_TYPE);
  const [[sortingType]] = useSearchParam(FILTER_TYPES.SORT_BY);
  const urlParams = new URLSearchParams(searchParams);
  const isAnyFilterSelected = SEARCH_FILTERS.some(filter => urlParams.has(filter));
  const mappedContentTypes = contentTypes === null || contentTypes === void 0 ? void 0 : contentTypes.map(type => type === 'COURSE' ? 'TRACK' : type);
  return {
    page: page || 1,
    setPage,
    isAnyFilterSelected,
    languages: languages !== null && languages !== void 0 && languages.length ? languages : [getLang().toUpperCase()],
    search: search !== null && search !== void 0 && search.length ? search : undefined,
    categories: categories !== null && categories !== void 0 && categories.length ? categories : undefined,
    durations: durations !== null && durations !== void 0 && durations.length ? durations : undefined,
    hubSpotSoftware: hubSpotSoftware !== null && hubSpotSoftware !== void 0 && hubSpotSoftware.length ? hubSpotSoftware : undefined,
    levels: levels !== null && levels !== void 0 && levels.length ? levels : undefined,
    contentTypes: mappedContentTypes !== null && mappedContentTypes !== void 0 && mappedContentTypes.length ? mappedContentTypes : undefined,
    sortingType,
    hasAwardsOnly: hasAwardsOnly === 'ONLY_SHOW_CERTIFICATE_COURSES' ? true : undefined,
    visibilityGroup: contentSource
  };
};
export const useCatalogSearchResults = ({
  partnersOnly,
  pageSize = PAGE_SIZE,
  contentTypes,
  skip
} = {}) => {
  var _searchCriteria$conte;
  const _useCatalogSearchPara = useCatalogSearchParams(),
    {
      page,
      setPage,
      isAnyFilterSelected
    } = _useCatalogSearchPara,
    searchCriteria = _objectWithoutPropertiesLoose(_useCatalogSearchPara, _excluded);
  const categoryGroup = searchCriteria.categories ? searchCriteria.categories[0] : undefined;
  if (categoryGroup && CategoriesSubSections[categoryGroup]) {
    searchCriteria.categories = CategoriesSubSections[categoryGroup];
  }
  const softwareGroup = searchCriteria.hubSpotSoftware ? searchCriteria.hubSpotSoftware[0] : undefined;
  if (softwareGroup && HubspotToolsSubSections[softwareGroup]) {
    searchCriteria.hubSpotSoftware = HubspotToolsSubSections[softwareGroup];
  }
  if (contentTypes && !((_searchCriteria$conte = searchCriteria.contentTypes) !== null && _searchCriteria$conte !== void 0 && _searchCriteria$conte.length)) {
    searchCriteria.contentTypes = contentTypes;
  }
  const variables = Object.assign({}, searchCriteria, {
    pageLength: pageSize,
    partnersOnly,
    shouldFilterDeprecated: true,
    offset: {
      cursor: (page - 1) * pageSize
    }
  });
  const {
    searchResults,
    searchResultsLoading,
    searchResultsError,
    totalCount,
    correlationId
  } = useSearch({
    variables,
    skip,
    scoringConfig: {
      nameBoost: 50,
      descriptionBoost: 25,
      professorsBoost: 10,
      introContentNameBoost: 15,
      introContentDescriptionBoost: 7.5,
      introContentBoost: 5,
      subContentNamesBoost: 12,
      subContentDescriptionsBoost: 6,
      subContentsBoost: 1.5,
      categoriesBoost: 3,
      hubspotSoftwareBoost: 2,
      levelsBoost: 1,
      weightProgressInProgress: 0.1,
      weightProgressCompleted: 1,
      includeRecencyDecay: true,
      includeUserProgress: true,
      recencyOffset: 'PT2160H0M',
      recencyScale: 'PT72H0M',
      recencyDecay: 0.7
    }
  });
  const {
    cardModels,
    cardModelsLoading,
    cardModelsError
  } = useLearningContentCardModels({
    items: (searchResults === null || searchResults === void 0 ? void 0 : searchResults.map(item => {
      const {
        id,
        contentType
      } = item;
      return {
        id: id,
        type: contentType === 'TEMPLATE' || contentType === 'EBOOK' || contentType === 'EXERCISE' ? 'LESSON' : contentType,
        language: 'language' in item ? item.language : undefined,
        resultMLOptionId: item.resultMLOptionId
      };
    })) || [],
    skip: !searchResults
  });
  return {
    results: cardModels,
    loading: searchResultsLoading || cardModelsLoading,
    error: searchResultsError || cardModelsError,
    page,
    setPage,
    totalCount,
    isAnyFilterSelected,
    searchCriteria,
    correlationId
  };
};